import { buildQueryParams, cleanParams } from "@/utils";

/**
 * Рабочие группы (Отделы). Модуль для работы с API
 * @module units
 */

import { maxPerPage } from "@/constants";

const MESSENGER_CONTACTS_CHANGED_TYPE = "teamtools-messenger-contacts-changed";

const notifyMessengerContactsChanged = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(MESSENGER_CONTACTS_CHANGED_TYPE));
};

/**
 * Получение списка всех РГ. Поддерживает пагинацию, поиск и фильтры
 * @param  {Object} params - Параметры запроса
 * @returns {Promise} - список РГ
 * @throws {Error} - ошибка при получении данных
 */
export const getList = async (params) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(`/api/units?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение полного списка РГ без пагинации
 * @param  {Object} params - Параметры запроса
 * @returns {Promise} - список РГ
 * @throws {Error} - ошибка при получении данных
 */
export const getUnitsOptions = async (params) => {
  const queryString = new URLSearchParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  }).toString();

  try {
    const response = await fetch(`/api/units?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка моих РГ (как пользователя) без пагинации
 * @param  {Object} params - Параметры запроса
 * @returns {Promise} - список РГ
 * @throws {Error} - ошибка при получении данных
 */
export const getEmployeeUnitsOptions = async (params) => {
  const queryString = new URLSearchParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  }).toString();

  try {
    const response = await fetch(`/api/employee/units?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение публичного списка всех РГ для любого пользователя
 * @param  {Object} params - Параметры запроса
 * @returns {Promise} - список РГ
 * @throws {Error} - ошибка при получении данных
 */
export const getAllUnits = async (params) => {
  const queryString = new URLSearchParams(params).toString();

  try {
    const response = await fetch(`/api/public/units?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.units;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение данных конкретной РГ по uuid
 * @param  {string} uuid - uuid РГ
 * @returns {Promise} - данные РГ
 * @throws {Error} - ошибка при получении данных
 */
export const getItem = async (uuid) => {
  try {
    const response = await fetch(`/api/unit?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.unit;
  } catch (err) {
    throw err;
  }
};

/**
 * Создание новой РГ
 * @param  {Object} item - данные для создания
 * @returns {Promise} - созданная РГ
 * @throws {Error} - ошибка при создании данных
 */
export const createItem = async (item) => {
  try {
    const response = await fetch("/api/unit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.unit;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление данных РГ
 * @param  {Object} item - данные для обновления
 * @returns {Promise} - обновленная РГ
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/unit", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.unit;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение статистики задач по сотруднику (доступен для администратора)
 * @param  {Object} params - данные для получения статистики
 * @returns {Promise} - данные сотрудника
 * @throws {Error} - ошибка при получении данных
 */
export const getEmployeeStatistics = async (params) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);
  try {
    const response = await fetch(`/api/employee/tasks/stats?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление данных РГ
 * @param  {Object} item - данные для удаления
 * @returns {Promise} - удаленная РГ
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteItem = async (item) => {
  try {
    const response = await fetch("/api/unit", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.unit;
  } catch (err) {
    throw err;
  }
};

/** Управление участниками РГ */

/**
 * Добавить участника в РГ
 * @param  {Object} item - данные для добавления
 * @returns {Promise} - добавленный участник
 * @throws {Error} - ошибка при добавлении участника
 */
export const addMember = async (item) => {
  try {
    const response = await fetch("/api/unitmember", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при добавлении участника. ${data.error || "Попробуйте снова"}`
      );
    }

    notifyMessengerContactsChanged();
    return data.member;
  } catch (err) {
    throw err;
  }
};

/**
 * Удалить участника из РГ
 * @param  {Object} item - данные для удаления
 * @returns {Promise} - удаленный участник
 * @throws {Error} - ошибка при удалении участника
 */
export const deleteMember = async (item) => {
  try {
    const response = await fetch("/api/unitmember", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении участника. ${data.error || "Попробуйте снова"}`
      );
    }

    notifyMessengerContactsChanged();
    return data.member;
  } catch (err) {
    throw err;
  }
};

/**
 * Изменить доступ к подчиненным РГ для участника
 * @param  {Object} emlpoyee_uuid - uuid участника
 * @param  {Object} unit_uuid - uuid РГ
 * @param  {boolean} can_access_child_groups - доступ к подчиненным РГ
 * @returns {Promise} - обновленный участник
 * @throws {Error} - ошибка при изменении доступа
 */
export const changeMemberAccess = async (
  employee_uuid,
  unit_uuid,
  can_access_child_groups
) => {
  try {
    const response = await fetch("/api/unitmember/access", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        employee_uuid,
        unit_uuid,
        can_access_child_groups,
      }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при изменении доступа. ${data.error || "Попробуйте снова"}`
      );
    }

    notifyMessengerContactsChanged();
    return data.member;
  } catch (err) {
    throw err;
  }
};

/**
 * Получить дерево рабочих групп
 * @returns {Promise} - дерево РГ
 * @throws {Error} - ошибка при получении данных
 */
export const getUnitsTree = async (params) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(`/api/units-tree?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    // Найти личную группу
    const personalUnit = data.tree.find((unit) => unit?.is_personal);
    // Отфильтровать остальные
    const otherUnits = data.tree.filter((unit) => !unit.is_personal);

    // Собрать итоговый массив: сначала личная, потом остальные
    return personalUnit ? [personalUnit, ...otherUnits] : otherUnits;
  } catch (err) {
    throw err;
  }
};

/**
 * Установить корневую рабочую группу (только для типа Подразделение, т.е. с флагом is_hierarchical=true)
 * @param {Object} item - данные
 * @param {string} item.uuid - идентификатор рабочей группы
 * @returns {Promise} -
 * @throws {Error} - ошибка при установке новых значений
 */
export const setRootInit = async (item) => {
  try {
    const response = await fetch("/api/unit/setroot", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};
